import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/api/client';
import {
  createSupplierBnPeriod,
  deleteSupplierBnPeriod,
  listSupplierBnPeriods,
  type SupplierBnPeriodOut,
} from '@/api/suppliers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { isCurrentPeriod, sortPeriodsAscending, validateNewPeriod } from '@/lib/bnPeriods';
import { bnSourceLabel, isManuallyEnteredBnSource } from '@/lib/bnSource';

import { useAuth } from '../auth/AuthContext';

/** Editable BN timeline for one supplier (INP-508). A supplier's CRA
 *  Business Number can change over time; the engine resolves the BN in
 *  effect on each transaction's bill_date from this history. Practitioners
 *  read + edit it here. Rendered only when the drawer resolved a DB
 *  `supplier_id` (legacy / non-DB runs have none). */
export function SupplierBnTimelineSection({ supplierId }: { supplierId: string }) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['supplier-bn-periods', supplierId, userKey],
    queryFn: async () => listSupplierBnPeriods(supplierId, (await getIdToken()) ?? ''),
  });

  const periods = query.data ? sortPeriodsAscending(query.data.items) : [];

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ['supplier-bn-periods', supplierId] });

  const remove = useMutation({
    mutationFn: async (periodId: string) =>
      deleteSupplierBnPeriod(supplierId, periodId, (await getIdToken()) ?? ''),
    onSuccess: invalidate,
  });

  return (
    <section className="grid gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">
          BN timeline
        </h3>
        <p className="text-[11px] text-brand-slate">
          The BN in effect on each transaction&rsquo;s date.
        </p>
      </div>

      {query.isPending ? (
        <p className="text-xs text-brand-slate">Loading BN history…</p>
      ) : query.isError ? (
        <p className="text-xs text-destructive">Failed to load BN history.</p>
      ) : periods.length === 0 ? (
        <p className="text-xs text-brand-slate">
          No BN periods recorded. Add one to date-range this supplier&rsquo;s Business Number.
        </p>
      ) : (
        <Table className="text-xs">
          <TableHeader>
            <TableRow>
              <TableHead className="px-2 py-1.5">From</TableHead>
              <TableHead className="px-2 py-1.5">To</TableHead>
              <TableHead className="px-2 py-1.5">BN</TableHead>
              <TableHead className="px-2 py-1.5">Source</TableHead>
              <TableHead className="px-2 py-1.5">Note</TableHead>
              <TableHead className="px-2 py-1.5 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="[&_td]:text-xs">
            {periods.map((p) => (
              <BnPeriodRow
                key={p.id}
                period={p}
                onDelete={() => remove.mutate(p.id)}
                deleting={remove.isPending && remove.variables === p.id}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {remove.isError && (
        <p className="text-xs text-destructive">{errorDetail(remove.error, 'Could not delete the period.')}</p>
      )}

      <AddBnPeriodForm supplierId={supplierId} existing={periods} onAdded={invalidate} />
    </section>
  );
}

function BnPeriodRow({
  period,
  onDelete,
  deleting,
}: {
  period: SupplierBnPeriodOut;
  onDelete: () => void;
  deleting: boolean;
}) {
  const current = isCurrentPeriod(period);
  // Two-step confirm: deleting a period changes bill-date BN resolution and
  // can repoint or clear the supplier's current BN, so a stray click
  // shouldn't do it. The first click arms; the second confirms.
  const [confirming, setConfirming] = useState(false);
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap px-2 py-1.5 font-mono">{period.valid_from}</TableCell>
      <TableCell className="whitespace-nowrap px-2 py-1.5 font-mono">
        {current ? (
          <Badge className="border-emerald-500/40 bg-emerald-50 text-emerald-900 hover:bg-emerald-50">
            current
          </Badge>
        ) : (
          period.valid_to
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap px-2 py-1.5 font-mono">{period.bn}</TableCell>
      <TableCell className="whitespace-nowrap px-2 py-1.5">
        <BnSourceBadge source={period.source} />
      </TableCell>
      <TableCell className="px-2 py-1.5 text-brand-slate">{period.note ?? '—'}</TableCell>
      <TableCell className="whitespace-nowrap px-2 py-1.5 text-right">
        {confirming ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-[11px] text-brand-slate">Delete{current ? ' current?' : '?'}</span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onDelete}
              disabled={deleting}
              className="h-6 px-2 text-xs text-destructive hover:text-destructive"
            >
              {deleting ? 'Removing…' : 'Confirm'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConfirming(false)}
              disabled={deleting}
              className="h-6 px-2 text-xs"
            >
              Cancel
            </Button>
          </span>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setConfirming(true)}
            className="h-6 px-2 text-xs text-destructive hover:text-destructive"
          >
            Delete
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/** Provenance of a BN period. The manually-entered (practitioner) source is
 *  highlighted so a hand-keyed BN stands apart from seeded / imported ones. */
function BnSourceBadge({ source }: { source: string }) {
  if (isManuallyEnteredBnSource(source)) {
    return (
      <Badge className="border-brand-royal/40 bg-brand-royal/10 text-brand-navy hover:bg-brand-royal/10">
        {bnSourceLabel(source)}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-border text-brand-slate">
      {bnSourceLabel(source)}
    </Badge>
  );
}

function AddBnPeriodForm({
  supplierId,
  existing,
  onAdded,
}: {
  supplierId: string;
  existing: SupplierBnPeriodOut[];
  onAdded: () => void;
}) {
  const { getIdToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [bn, setBn] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [note, setNote] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () =>
      createSupplierBnPeriod(
        supplierId,
        {
          bn: bn.trim(),
          valid_from: validFrom,
          valid_to: validTo || null,
          note: note.trim() || null,
        },
        (await getIdToken()) ?? '',
      ),
    onSuccess: () => {
      setBn('');
      setValidFrom('');
      setValidTo('');
      setNote('');
      setClientError(null);
      setOpen(false);
      onAdded();
    },
  });

  const reset = () => {
    setClientError(null);
    create.reset();
  };

  const submit = () => {
    const candidate = { bn, valid_from: validFrom, valid_to: validTo || null };
    const check = validateNewPeriod(candidate, existing);
    if (!check.ok) {
      setClientError(check.reason);
      return;
    }
    setClientError(null);
    create.mutate();
  };

  if (!open) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          reset();
          setOpen(true);
        }}
        className="justify-self-start text-xs"
      >
        + Add BN period
      </Button>
    );
  }

  return (
    <div className="grid gap-2 rounded-md border border-border bg-muted/20 px-3 py-2.5">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="grid gap-1 text-[11px] text-brand-slate">
          BN
          <input
            type="text"
            value={bn}
            onChange={(e) => setBn(e.target.value)}
            placeholder="123456789RT0001"
            className="rounded border border-border bg-background px-2 py-1 font-mono text-xs text-brand-navy"
          />
        </label>
        <label className="grid gap-1 text-[11px] text-brand-slate">
          Note (optional)
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. amalgamation"
            className="rounded border border-border bg-background px-2 py-1 text-xs text-brand-navy"
          />
        </label>
        <label className="grid gap-1 text-[11px] text-brand-slate">
          Valid from
          <input
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 text-xs text-brand-navy"
          />
        </label>
        <label className="grid gap-1 text-[11px] text-brand-slate">
          Valid to (blank = current)
          <input
            type="date"
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 text-xs text-brand-navy"
          />
        </label>
      </div>

      {(clientError || create.isError) && (
        <p className="text-xs text-destructive">
          {clientError ?? errorDetail(create.error, 'Could not add the period.')}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={submit}
          disabled={create.isPending}
          className="text-xs"
        >
          {create.isPending ? 'Adding…' : 'Add period'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          className="text-xs"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** Pull the FastAPI `{detail}` string off an ApiError, else a fallback. */
function errorDetail(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.body && typeof error.body === 'object') {
    const detail = (error.body as { detail?: unknown }).detail;
    if (typeof detail === 'string') return detail;
  }
  return fallback;
}
